import { useCallback, useEffect, useState } from 'react'

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'theme'

/** Read the user's pinned theme from localStorage, if any. */
function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === 'dark' || value === 'light') return value
  } catch {
    /* Storage may throw in private-browse modes — fall through. */
  }
  return null
}

/** Resolve the OS preference. Defaults to dark when the API is missing. */
function systemPreference(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/**
 * Reactive theme manager.
 *
 * Initial value: stored pin > system preference > 'dark'.
 *
 * Setting a theme via {@link setTheme} or {@link toggle} pins it to
 * localStorage; subsequent OS preference changes are ignored. To go
 * back to "follow the OS," delete the localStorage entry manually
 * (no UI affordance for that today — system-preference follow is the
 * default for never-pinned users only).
 *
 * The `<html data-theme="...">` attribute is the side effect this
 * hook produces. CSS in styles/index.css handles every visual change.
 */
export function useTheme(): {
  theme: Theme
  setTheme: (next: Theme) => void
  toggle: () => void
} {
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? systemPreference())

  // Sync the resolved theme onto <html> + persist when pinned.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Follow the OS preference live for users who haven't pinned anything.
  // The pin-check inside the handler runs on every OS-preference change,
  // so a user who pins mid-session via setTheme() stops auto-following
  // immediately — without us having to tear down + re-subscribe the
  // listener on every theme change.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mql = window.matchMedia('(prefers-color-scheme: light)')
    const handler = (e: MediaQueryListEvent) => {
      if (readStored() !== null) return // user has pinned — don't auto-follow
      setThemeState(e.matches ? 'light' : 'dark')
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    setThemeState(next)
  }, [])

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }, [theme, setTheme])

  return { theme, setTheme, toggle }
}
